import React, { ReactElement } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import copy from 'copy-to-clipboard';
import i18n from 'i18next';
import { Trans } from 'react-i18next';
import { NavigateFunction } from 'react-router-dom';
import { ColumnsType } from 'common/components/antd/Table/Table';
import { Tag } from 'common/components/antd/Tag/Tag';
import { Typography } from 'common/components/antd/Typography/Typography';
import { TruncatedCopyableTextCell } from 'common/components/TruncatedCopyableCell/TruncatedCopyableCell';
import { formatDateFromISO } from 'common/utils/format.utils';
import { AccountKindToFancy, TAccountKind } from 'domains/accounts/types/accounts.types';
import { RenderIBAN } from 'domains/accounts/utils/render-account-iban';
import { EReportingPaymentsColumnKeys } from 'domains/reporting/enum/reporting.enum';
import { EReportingPaymentStatus, TReportingPaymentsEntity } from 'domains/reporting/types/reporting.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { ReportingPaymentsGetParams } from '../enums/payments.enum';
import { TPaymentsTableRecord } from '../types/payments.types';

const getStatusTagColor = (status: string): string => {
  switch (status) {
    case EReportingPaymentStatus.Created:
      return '#262626';
    case EReportingPaymentStatus.Confirmed:
      return '#1890FF';
    case EReportingPaymentStatus.Failed:
      return '#F5222D';
    case EReportingPaymentStatus.Canceled:
      return '#F5222D';
    case EReportingPaymentStatus.Refunded:
      return '#13C2C2';
    case EReportingPaymentStatus.Scheduled:
      return '#FFC53D';
    case EReportingPaymentStatus.Completed:
      return '#52C41A';
    default:
      return 'processing';
  }
};

const copyPaymentURL = (guid: string): void => {
  const url = `${window.location.origin}${ROUTES_MAPPING.PROTECTED.REPORTING_PAYMENT_OPERATIONS}`;
  copy(`${url}?${ReportingPaymentsGetParams.ModalPaymentInformationGUID}=${guid}`);
};

export const getColumns = (
  {
    navigate,
    onCellClickHandler,
  }: {
    navigate: NavigateFunction,
    onCellClickHandler: (record: TPaymentsTableRecord, cellId: string) => void
  }): ColumnsType<TPaymentsTableRecord> => [
  {
    title: <Trans i18nKey="reporting.paymentOperations.table.column.guid.title" />,
    width: 110,
    key: EReportingPaymentsColumnKeys.Guid,
    dataIndex: EReportingPaymentsColumnKeys.Guid,
    render: (text, record, index) => (
      <TruncatedCopyableTextCell
        onLinkClick={(): void => {
          copyPaymentURL(record.guid);
        }}
        rowIndex={index}
        linkTooltipText={i18n.t('common.copy') as string}
      >
        {record.guid}
      </TruncatedCopyableTextCell>
    ),
  },
  {
    title: <Trans i18nKey="reporting.paymentOperations.table.column.header.sender" />,
    children: [
      {
        title: (
          <Trans
            i18nKey= "reporting.paymentOperations.table.column.senderCounterparty.title"
          />
        ),
        width: 97,
        key: EReportingPaymentsColumnKeys.SenderCounterparty,
        dataIndex: EReportingPaymentsColumnKeys.SenderCounterparty,
      },
      {
        title: (
          <Trans
            i18nKey="reporting.paymentOperations.table.column.senderAccountKind.title"
          />
        ),
        width: 50,
        key: EReportingPaymentsColumnKeys.SenderAccountKind,
        dataIndex: EReportingPaymentsColumnKeys.SenderAccountKind,
        render: (value: TAccountKind) => AccountKindToFancy[value],
      },
      {
        title: <Trans i18nKey="reporting.paymentOperations.table.column.senderId.title" />,
        width: 97,
        key: EReportingPaymentsColumnKeys.SenderID,
        dataIndex: EReportingPaymentsColumnKeys.SenderID,
        render: (value: string, record: TReportingPaymentsEntity, index: number): ReactElement =>
          <RenderIBAN value={record.sender_id} index={index} />,
      },
    ],
  },
  {
    title: <Trans i18nKey="reporting.paymentOperations.table.column.header.receiver" />,
    children: [
      {
        title: (
          <Trans
            i18nKey="reporting.paymentOperations.table.column.receiverCounterparty.title"
          />
        ),
        width: 97,
        key: EReportingPaymentsColumnKeys.ReceiverCounterparty,
        dataIndex: EReportingPaymentsColumnKeys.ReceiverCounterparty,
      },
      {
        title: (
          <Trans
            i18nKey="reporting.paymentOperations.table.column.receiverAccountKind.title"
          />
        ),
        width: 50,
        key: EReportingPaymentsColumnKeys.ReceiverAccountKind,
        dataIndex: EReportingPaymentsColumnKeys.ReceiverAccountKind,
        render: (value: TAccountKind) => AccountKindToFancy[value],
      },
      {
        title: (
          <Trans i18nKey="reporting.paymentOperations.table.column.receiverId.title" />
        ),
        width: 97,
        key: EReportingPaymentsColumnKeys.ReceiverID,
        dataIndex: EReportingPaymentsColumnKeys.ReceiverID,
        render: (value: string, record: TReportingPaymentsEntity, index: number): ReactElement =>
          <RenderIBAN value={record.receiver_id} index={index} />,
      },
    ],
  },
  {
    title: <Trans i18nKey="reporting.paymentOperations.table.column.amount.title" />,
    width: 150,
    key: EReportingPaymentsColumnKeys.Amount,
    dataIndex: EReportingPaymentsColumnKeys.Amount,
    render: (text, record) => (
      <Typography.Text style={{ whiteSpace: 'nowrap' }}>
        {text} {record.asset_code}
      </Typography.Text>
    ),
  },
  {
    title: <Trans i18nKey="reporting.paymentOperations.table.column.fee.title" />,
    width: 120,
    key: EReportingPaymentsColumnKeys.Fee,
    dataIndex: EReportingPaymentsColumnKeys.Fee,
    render: (text, record) => (
      <Typography.Text style={{ whiteSpace: 'nowrap' }}>
        {text} {record.asset_code}
      </Typography.Text>
    ),
  },
  {
    title: <Trans i18nKey="reporting.paymentOperations.table.column.status.title" />,
    width: 115,
    key: EReportingPaymentsColumnKeys.Status,
    dataIndex: EReportingPaymentsColumnKeys.Status,
    render: (value) => (
      <Tag color={getStatusTagColor(value)}>
        <Trans
          i18nKey={`reporting.paymentOperations.table.column.status.value.${value.toLowerCase()}`} defaults={value} />
      </Tag>
    ),
  },
  {
    title: <Trans i18nKey="reporting.paymentOperations.table.column.createdAt.title" />,
    width: 115,
    key: EReportingPaymentsColumnKeys.CreatedAt,
    dataIndex: EReportingPaymentsColumnKeys.CreatedAt,
    render: (value) => formatDateFromISO(value),
  },
  {
    title: <Trans i18nKey="reporting.paymentOperations.table.column.details.title" />,
    width: 50,
    key: EReportingPaymentsColumnKeys.Details,
    dataIndex: EReportingPaymentsColumnKeys.Details,
    onCell: (record: TReportingPaymentsEntity) => ({
      onClick: () => onCellClickHandler(record, EReportingPaymentsColumnKeys.Details),
    }),
    render: (_: string, record: TReportingPaymentsEntity) => (
      <InfoCircleOutlined
        style={{ cursor: 'pointer' }}
        onClick={(): void => {
          const url = ROUTES_MAPPING.PROTECTED.REPORTING_PAYMENT_OPERATIONS;
          navigate(`${url}?${ReportingPaymentsGetParams.ModalPaymentInformationGUID}=${record.guid || ''}`);
        }}
      />
    ),
    fixed: 'right',
  },
];
